// filepath: apps/web/src/admin/locations/LocationForm.tsx
//
// ISS-515 — shared location create/edit form component.
//
// Reused by new.tsx and edit.tsx. The component is a controlled
// form with three fields:
//   - parent_location_id (optional, root / id-of-another-location;
//     the form filters out `selfId` for the edit screen -- the
//     BA spec forbids self-parent).
//   - name (required, maxLength=255 mirroring the Go Validate
//     function at internal/directory/location/package.go:57-65).
//   - code (optional, maxLength=64).
//
// INV-9: no dangerouslySetInnerHTML -- every value flows through
// JSX text / <option value>.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { useDirtyTrackedFields } from "@/lib/useDirtyTrackedFields";
import type { Location } from "@/features/directory/types";

export interface LocationFormValues {
  parent_location_id: string | null;
  name: string;
  code: string;
}

/**
 * The shape handed to `useDirtyTrackedFields`. A `type` alias, not the
 * exported `interface` above: only the former gets an implicit index
 * signature, which the hook's `T extends Record<string, unknown>`
 * bound needs. It is also the shape the two string-valued `<select>`s
 * and inputs actually want — the server's `null` parent is "" here,
 * per field, which is presentation normalisation and NOT the old
 * sibling coupling.
 */
type LocationFormFields = {
  parent_location_id: string;
  name: string;
  code: string;
};

export interface LocationFormProps {
  /** Initial form values. For new, default to {name:'', code:'',
   * parent_location_id:null}. For edit, set from the loader. */
  initial: LocationFormValues;
  /** Optional: the id of the row being edited (so the parent
   * picker can filter `selfId` out). */
  selfId?: string;
  /** Optional list of available parent candidates. The new
   * screen fetches this via listLocations(); the edit screen
   * does the same. If `undefined`, the parent picker renders
   * only the "Root" option (defensive fallback for tests). */
  parentCandidates?: Location[];
  /** Submit handler. The form passes the trimmed values; the
   * page wires this to createLocation / updateLocation. */
  onSubmit: (values: LocationFormValues) => Promise<void>;
  /** Optional cancel handler (navigates back). */
  onCancel?: () => void;
  /** When true, the submit button renders as "Saving…". */
  submitting?: boolean;
  /** Optional error banner (raw error string). */
  error?: string | null;
  /** When set, the parent picker has a field-level
   * validation error (e.g. server returned cycle-rejected). */
  parentError?: string | null;
  /** Title for the submit button. */
  submitLabel: string;
  /** Title for the loading state. */
  loadingLabel: string;
  /** Heading for the form (e.g. "New location"). */
  heading: string;
}

export function LocationForm({
  initial,
  selfId,
  parentCandidates,
  onSubmit,
  onCancel,
  submitting,
  error,
  parentError,
  submitLabel,
  loadingLabel,
  heading,
}: LocationFormProps): React.ReactElement {
  const { t } = useTranslation();

  // ISS-805 — per-field dirty tracking replaces the whole-state re-seed effect
  // that used to sit here on [initial.parent_location_id, initial.name,
  // initial.code]. That effect reset ALL THREE fields when ANY ONE dep moved,
  // so re-parenting a location in another session wiped a name the operator
  // was mid-way through typing. Each field now re-seeds only on its OWN
  // server value, and never once it has been edited (see
  // lib/useDirtyTrackedFields for the BA ruling).
  //
  // `recordKey` is the record identity, not the `initial` object (a new
  // identity every render would re-seed on every render). `selfId` IS the
  // row's id on the edit screen; create has none.
  const fields = useDirtyTrackedFields<LocationFormFields>(
    {
      parent_location_id: initial.parent_location_id ?? "",
      name: initial.name,
      code: initial.code,
    },
    `location:${selfId ?? "new"}`,
  );

  // BA rule 4 — a non-blocking notice, never a dialog: when the server value
  // moved underneath a field the operator HAS edited, their text is kept and
  // the server's value is shown beside it. Dismiss clears it per field.
  const conflictFor = (
    id: string,
    label: string,
    field: {
      serverConflictValue: string | undefined;
      acknowledgeServerConflict: () => void;
    },
  ): {
    id: string;
    label: string;
    value: string;
    dismiss: () => void;
  } | null =>
    field.serverConflictValue === undefined
      ? null
      : {
          id,
          label,
          value: field.serverConflictValue,
          dismiss: field.acknowledgeServerConflict,
        };

  const serverConflicts = [
    conflictFor(
      "parent_location_id",
      t("directory.locations.form.parent", "Parent location"),
      fields.parent_location_id,
    ),
    conflictFor(
      "name",
      t("directory.locations.form.name", "Name"),
      fields.name,
    ),
    conflictFor(
      "code",
      t("directory.locations.form.code", "Code (optional)"),
      fields.code,
    ),
  ].filter((c): c is NonNullable<typeof c> => c !== null);

  return (
    <section data-testid="location-form-screen">
      <header>
        <h1>{heading}</h1>
      </header>
      {error ? (
        // design-adherence-ignore: <Banner> doesn't accept data-testid;
        // hook the test id on the wrapping div instead.
        <div data-testid="location-form-error">
          <Banner tone="danger">
            {t("directory.locations.form.error", "Error: {{message}}", {
              message: error,
            })}
          </Banner>
        </div>
      ) : null}
      {serverConflicts.length > 0 ? (
        <div data-testid="location-form-server-conflicts">
          {serverConflicts.map((c) => (
            <Banner key={c.id} tone="warning" onDismiss={c.dismiss}>
              {t(
                "directory.locations.form.server_conflict",
                "{{field}} changed on the server while you were editing. Your text was kept — the server now holds: {{value}}",
                { field: c.label, value: c.value },
              )}
            </Banner>
          ))}
        </div>
      ) : null}
      <Card>
        <form
          aria-label={heading}
          onSubmit={async (e) => {
            e.preventDefault();
            await onSubmit({
              parent_location_id:
                fields.parent_location_id.value === ""
                  ? null
                  : fields.parent_location_id.value,
              name: fields.name.value.trim(),
              code: fields.code.value.trim(),
            });
          }}
        >
          <div>
            <label htmlFor="location-form-parent">
              {t("directory.locations.form.parent", "Parent location")}
            </label>
            <select
              id="location-form-parent"
              value={fields.parent_location_id.value}
              onChange={(e) => fields.parent_location_id.set(e.target.value)}
              aria-invalid={parentError ? true : undefined}
              aria-describedby={
                parentError ? "location-form-parent-error" : undefined
              }
              data-testid="location-form-parent"
            >
              {/* Root -- null parent_location_id (a top-level
                  location with no parent). */}
              <option value="">
                {t("directory.locations.form.parent_root", "— Root —")}
              </option>
              {(parentCandidates ?? [])
                // BA spec: never let the row be its own parent
                // (server enforces cycles via /move, but the
                // form prevents accidental self-parent on
                // create / edit parent-pick).
                .filter((p) => p.id !== selfId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.code ? ` (${p.code})` : ""}
                  </option>
                ))}
            </select>
            {parentError ? (
              <p
                role="alert"
                id="location-form-parent-error"
                data-testid="location-form-parent-error"
              >
                {parentError}
              </p>
            ) : null}
          </div>
          <div>
            <label htmlFor="location-form-name">
              {t("directory.locations.form.name", "Name")}
            </label>
            <input
              id="location-form-name"
              required
              minLength={1}
              maxLength={255}
              value={fields.name.value}
              onChange={(e) => fields.name.set(e.target.value)}
              data-testid="location-form-name"
            />
          </div>
          <div>
            <label htmlFor="location-form-code">
              {t("directory.locations.form.code", "Code (optional)")}
            </label>
            <input
              id="location-form-code"
              maxLength={64}
              value={fields.code.value}
              onChange={(e) => fields.code.set(e.target.value)}
              data-testid="location-form-code"
            />
          </div>
          <div className="location-form-actions">
            {/* design-adherence-ignore: <Button> doesn't accept
                data-testid; wrap in a span to keep the test hook. */}
            <span data-testid="location-form-submit">
              <Button
                type="submit"
                variant="primary"
                disabled={submitting || fields.name.value.trim().length === 0}
              >
                {submitting ? loadingLabel : submitLabel}
              </Button>
            </span>
            {onCancel ? (
              <span data-testid="location-form-cancel">
                <Button type="button" variant="secondary" onClick={onCancel}>
                  {t("directory.locations.form.cancel", "Cancel")}
                </Button>
              </span>
            ) : null}
          </div>
        </form>
      </Card>
    </section>
  );
}
